import { ExternalLinkIcon } from "@heroicons/react/outline";
import { type ComponentProps } from "react";
import { Link } from "react-router-dom";

import TraceSpanHierarchy from "./TraceSpanHierarchy";
import { useTraceDetailState } from "./hooks/useTraceDetailState";

import { Skeleton } from "@/components/ui/skeleton";
import { useUser } from "@/context/user-context";

interface TraceCellBodyProps {
  isLoading: boolean;
  error: unknown;
  hierarchy: ComponentProps<typeof TraceSpanHierarchy>;
}

const TraceCellBody = ({ isLoading, error, hierarchy }: TraceCellBodyProps) => {
  if (isLoading) {
    return (
      <div className="space-y-2 p-4">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }

  if (error) {
    return <p className="p-4 text-sm text-gray-500">Could not load this trace.</p>;
  }

  return <TraceSpanHierarchy {...hierarchy} />;
};

/**
 * A run's trace, rendered inside a cell with the same span table as the trace detail page. Spans are fetched by `traceId` so the table gets raw spans — the cell's stored
 * snapshot is display-shaped and would lose the attributes the table derives span types from.
 */
export const MockTraceCellView = ({ traceId }: { traceId: string }) => {
  const workspaceId = useUser()?.activeWorkspaceId;
  const {
    error,
    flattenedSpans,
    isHighlighted,
    isLoading,
    setSelectedSpan,
    setTimeRange,
    spanStatuses,
    spanTree,
    table,
    timeRange,
  } = useTraceDetailState(traceId);

  return (
    <div className="flex flex-col pt-4">
      <div className="mb-2 flex items-center justify-end">
        {workspaceId && (
          <Link
            to={`/workspace/${workspaceId}/traces/${traceId}`}
            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
          >
            Open full trace
            <ExternalLinkIcon className="h-3.5 w-3.5" />
          </Link>
        )}
      </div>
      <div className="h-[32rem] overflow-hidden rounded-lg border border-gray-200">
        <TraceCellBody
          isLoading={isLoading}
          error={error}
          hierarchy={{
            flattenedSpans,
            isHighlighted,
            setSelectedSpan,
            setTimeRange,
            spanStatuses,
            spanTree,
            table,
            timeRange,
          }}
        />
      </div>
    </div>
  );
};
